import { format } from "date-fns";
import type { FailClass, IntakeCase } from "@/types/intake";
import { FAIL_CLASS_LABEL, FAIL_CLASS_TONE } from "@/types/intake";
import type { EnumBadgeSemanticTone } from "@/types";

/**
 * Parse a "yyyy-MM-dd" string as a local calendar date (midnight local time).
 *
 * NOTE: do NOT use ``parseDate`` from ``@/lib/dates`` here — it routes
 * "yyyy-MM-dd" through ``new Date(string)``, which the JS spec parses as UTC
 * midnight. In negative-UTC offsets (e.g. EDT) that lands on the *previous*
 * calendar day in local time, so any subsequent ``format()`` call produces a
 * day-off display like "Wednesday, May 6" for "2026-05-07".
 */
function parseYmdLocal(ymd: string): Date | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(ymd);
  if (!m) return null;
  const y = Number(m[1]);
  const mo = Number(m[2]);
  const d = Number(m[3]);
  if (!Number.isFinite(y) || !Number.isFinite(mo) || !Number.isFinite(d)) {
    return null;
  }
  return new Date(y, mo - 1, d);
}

export function formatLocalDateLabel(ymd: string): string {
  const d = parseYmdLocal(ymd);
  if (!d) return ymd;
  try {
    return format(d, "EEE MMM d");
  } catch {
    return ymd;
  }
}

export function formatLocalDateLong(ymd: string): string {
  const d = parseYmdLocal(ymd);
  if (!d) return ymd;
  try {
    return format(d, "EEEE, MMMM d, yyyy");
  } catch {
    return ymd;
  }
}

export function failClassLabel(fc: FailClass | string): string {
  return FAIL_CLASS_LABEL[(fc as FailClass) ?? "other"] ?? String(fc);
}

export function failClassTone(fc: FailClass | string): EnumBadgeSemanticTone {
  return FAIL_CLASS_TONE[(fc as FailClass) ?? "other"] ?? "neutral";
}

export function caseDisplayName(c: Pick<IntakeCase, "member_first_name" | "member_last_name">): string {
  return [c.member_first_name, c.member_last_name].filter(Boolean).join(" ") || "—";
}

export function shortCaseId(id: string, max: number = 12): string {
  if (!id) return "—";
  return id.length > max ? `${id.slice(0, max)}…` : id;
}

/**
 * Format an ISO timestamp (e.g. ``outbound_scheduled_for``) for display in
 * the viewer's local timezone, e.g. ``May 7, 11:42 AM``. Falls back to the
 * raw string if it can't be parsed.
 */
export function formatScheduledFor(iso: string | null | undefined): string | null {
  if (!iso) return null;
  const s = String(iso).trim();
  if (!s) return null;
  const d = new Date(s);
  if (isNaN(d.getTime())) return s;
  try {
    return format(d, "MMM d, h:mm a");
  } catch {
    return s;
  }
}

/** Increment ymd by N days (preserves YYYY-MM-DD format). */
export function shiftYmd(ymd: string, deltaDays: number): string {
  // Parse "yyyy-MM-dd" as a calendar date and shift in UTC so the result is
  // independent of the viewer's local timezone. Using parseDate + setDate
  // here is unsafe because `new Date("yyyy-MM-dd")` parses as UTC midnight
  // but setDate/getDate operate in local time, so in negative-UTC offsets
  // a +1 day shift can return the same calendar day.
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(ymd);
  if (!m) return ymd;
  const y = Number(m[1]);
  const mo = Number(m[2]);
  const d = Number(m[3]);
  const utc = new Date(Date.UTC(y, mo - 1, d));
  utc.setUTCDate(utc.getUTCDate() + deltaDays);
  const yyyy = utc.getUTCFullYear();
  const mm = String(utc.getUTCMonth() + 1).padStart(2, "0");
  const dd = String(utc.getUTCDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}
